const COPING_STYLES = [
  { key: "surrender", label: "Surrender", blurb: "I gave in to it as if it were true" },
  { key: "avoid", label: "Avoid", blurb: "I ran from it, numbed out or shut down" },
  { key: "overcompensate", label: "Overcompensate", blurb: "I fought it by going to the opposite extreme" },
  { key: "healthy", label: "Healthy Adult response", blurb: "I responded from my adaptive schema" }
];
function copingStyleLabel(k) {
  return COPING_STYLES.find((c) => c.key === k)?.label ?? null;
}
const CORE_NEEDS = [
  {
    key: "attachment",
    label: "Secure Attachment to Others",
    domain: "Disconnection & Rejection",
    blurb: "Covering safety, stability, nurturance, acceptance, and belonging.",
    items: [
      { need: "To be seen and heard", says: "I don't feel seen, heard, or acknowledged by the people around me." },
      { need: "To be understood", says: "I don't feel like anyone truly gets me or comprehends my perspective." },
      { need: "To be validated", says: "I don't feel like my feelings or experiences matter or are taken seriously." },
      { need: "To be loved and wanted", says: "I don't feel chosen, desired, or unconditionally loved for who I am." },
      { need: "To feel connected", says: "I don't feel a sense of closeness, warmth, or meaningful bridge to others." },
      { need: "To feel safe and secure", says: "I don't feel secure or protected in my relationships; I feel like support could vanish at any moment." },
      { need: "Jungian: Relating Ego to the Self", says: "I don't feel connected to an inner compass or a deeper sense of containment; my conscious mind is entirely run by stress and isolation." }
    ]
  },
  {
    key: "autonomy",
    label: "Autonomy, Competence, and a Sense of Identity",
    domain: "Impaired Autonomy & Performance",
    blurb: "Covering independence, capability, and a coherent sense of self.",
    items: [
      { need: "Independence to become my own person", says: "I don't feel free or capable of making my own life choices without interference." },
      { need: "Challenge and stimulation", says: "I don't feel mentally stimulated or stretched in a healthy, engaging way." },
      { need: "Encouragement to develop competence", says: "I don't feel backed up or believed in when I try to learn new things or build mastery." },
      { need: "Opportunities for self-development", says: "I don't feel like I have the space or resources to grow into my full potential." },
      { need: "To receive constructive feedback", says: "I don't receive honest, supportive guidance that helps me improve without harsh judgment." },
      { need: "Jungian: Balancing Opposites (Anima/Animus)", says: "I don't feel internally balanced; I keep swinging between extreme dependence and total isolation." }
    ]
  },
  {
    key: "expression",
    label: "Freedom to Express Valid Needs and Emotions",
    domain: "Other-Directedness",
    blurb: "Covering the right to voice feelings, desires, and thoughts without fear.",
    items: [
      { need: "To express how I feel or think", says: "I don't feel safe voicing my genuine opinions or emotional state without fear of backlash." },
      { need: "To express what I like or dislike", says: "I don't feel allowed to have personal preferences without being judged or overridden." },
      { need: "To ask for what I need or want", says: "I don't feel worthy or safe making direct requests for my own support." },
      { need: "To make my own choices", says: "I don't feel trusted to make decisions for my own life." },
      { need: "To disagree skilfully", says: "I don't feel safe having a conflict or a different viewpoint without the relationship breaking." },
      { need: "Jungian: Confronting and Integrating the Shadow", says: "I don't feel safe acknowledging my anger, flaws, or vulnerability; I hide or judge those parts of myself." }
    ]
  },
  {
    key: "play",
    label: "Spontaneity and Play",
    domain: "Overvigilance & Inhibition",
    blurb: "Covering joy, humor, creativity, relaxation, and intrinsic self-worth.",
    items: [
      { need: "Space and time for fun and silliness", says: "I don't feel allowed to relax, play, or be lighthearted without guilt or pressure." },
      { need: "Acceptance of self-expression", says: "I don't feel free to express my creativity or weirdness without worrying how it looks." },
      { need: "Exploration and adventure", says: "I don't feel safe stepping out of rigid routines to explore the unknown." },
      { need: "Imagination and creativity", says: "I don't feel connected to my imagination; everything feels blocked by performance pressure." },
      { need: "Building up positive emotions", says: "I don't feel like I experience joy, ease, or lightness \u2014 life feels like a constant test of survival." },
      { need: "Jungian: Shedding the Persona", says: "I don't feel safe dropping my social mask or performing; I feel like I have to wear armor and meet external metrics to be acceptable." }
    ]
  },
  {
    key: "limits",
    label: "Realistic Limits and Self-Control",
    domain: "Impaired Limits",
    blurb: "Covering internal discipline, frustration tolerance, responsibility, and respect for others.",
    items: [
      { need: "To practise self-control", says: "I don't feel grounded enough to pause before reacting to my impulses." },
      { need: "To be self-disciplined", says: "I don't feel equipped to sustain focus or stick to long-term commitments when discomfort arises." },
      { need: "To take responsibility for my actions", says: "I don't feel accountable or capable of owning my mistakes without collapsing into shame." },
      { need: "To respect a response of NO from others", says: "I don't feel able to gracefully accept boundaries when someone pushes back against me." },
      { need: "To accept another person's right to safety, privacy and space", says: "I don't feel spacious enough inside to let others have their own boundaries without taking it personally." },
      { need: "Jungian: Finding Meaning and Purpose", says: "I don't feel a sense of deeper soul destiny or alignment; everything feels dry, mechanical, or constrained by rigid survival rules." }
    ]
  }
];
function needStatements(keys) {
  return keys.map((k) => {
    const [g, ...rest] = k.split(":");
    const need = rest.join(":");
    return CORE_NEEDS.find((c) => c.key === g)?.items.find((i) => i.need === need)?.says ?? need;
  }).filter(Boolean);
}
function coreNeed(key) {
  return CORE_NEEDS.find((n) => n.key === key) ?? null;
}
const needGroups = (needs) => [...new Set(needs.map((n) => n.split(":")[0]))];
const needLabel = (n) => n.split(":").slice(1).join(":");
const SCHEMA_SCRIPTS = {
  "Abandonment / Instability": {
    script: "Everyone I care about will eventually leave or pull away.",
    surrender: "I clung tightly or put up with poor treatment so they wouldn't leave.",
    avoid: "I kept my distance so no one could leave me first.",
    overcompensate: "I got controlling or ended things before I could be hurt.",
    adaptive: "Secure Attachment & Relational Permanence",
    adaptiveScript: "Distance or conflict for a moment doesn't mean the end of a connection.",
    flip: "Build trust in stable, predictable relationships \u2014 temporary space is not abandonment.",
    reparent: "I'm not going anywhere. Even when people step away for a while, you still have me \u2014 and you are worth staying for.",
    actions: ["Wait 1 hour before sending a reassurance text", "Name one person who has stayed", "Plan a calm check-in instead of a test"]
  },
  "Mistrust / Abuse": {
    script: "People will hurt, use or take advantage of me if I let my guard down.",
    surrender: "I let myself be taken advantage of, or chose people who repeat old harm.",
    avoid: "I shut people out and refused to open up at all.",
    overcompensate: "I got suspicious, interrogated or attacked first.",
    adaptive: "Healthy Discernment & Safe Trust",
    adaptiveScript: "I can protect my boundaries and still stay open to safe people.",
    flip: "Replace absolute cynicism with calibrated trust \u2014 check the evidence about this person, now.",
    reparent: "You were hurt before, and your guard kept you safe. Now I'm here to watch out for you, so you don't have to carry it all alone.",
    actions: ["List 3 facts about how this person has actually treated me", "Share one small, low-risk thing", "Set one clear boundary out loud"]
  },
  "Emotional Deprivation": {
    script: "No one will ever really care for me or meet my needs, so why ask.",
    surrender: "I didn't ask for comfort and accepted feeling uncared for.",
    avoid: "I pretended I don't need anyone.",
    overcompensate: "I demanded attention or tested people to prove they care.",
    adaptive: "Emotional Fulfillment & Reciprocity",
    adaptiveScript: "My needs are valid, and I can voice them to people who are safe.",
    flip: "Practise mutual warmth: name exactly what you need and let a safe person meet you there.",
    reparent: "Your need for care is not too much. I see you, I hear you, and I'm going to take your feelings seriously.",
    actions: ["Ask one safe person for one specific thing", "Accept a kind gesture without deflecting", "Give myself 10 minutes of comfort"]
  },
  "Defectiveness / Shame": {
    script: "I'm deeply flawed, bad and unlovable at my core.",
    surrender: "I accepted the criticism as proof and drifted toward critical people.",
    avoid: "I hid my real self and stayed quiet.",
    overcompensate: "I got defensive, perfectionistic or critical of others.",
    adaptive: "Unconditional Self-Worth & Self-Acceptance",
    adaptiveScript: "My mistakes and flaws are human \u2014 they don't touch my inherent dignity.",
    flip: "Meet your whole self, mistakes included, with self-respect. Shame shrinks in the light.",
    reparent: "There is nothing wrong with who you are. You are lovable exactly as you are, even on the hard days.",
    actions: ["Write 3 things I respect about myself", "Let one trusted person see something real", "Speak to myself as I would to a friend"]
  },
  "Social Isolation / Alienation": {
    script: "I'm fundamentally different \u2014 I don't belong anywhere.",
    surrender: "I resigned myself to loneliness and read neutral signals as rejection.",
    avoid: "I withdrew, skipped plans or ghosted.",
    overcompensate: "I tried too hard to fit in or acted overly confident to mask it.",
    adaptive: "Secure Belonging & Community Connection",
    adaptiveScript: "A mismatch is about alignment, not my worth. My uniqueness is a gift.",
    flip: "Cultivate shared humanity and seek spaces where your true perspective is welcome.",
    reparent: "You belong. You don't have to change who you are to be wanted \u2014 the right people will love the real you.",
    actions: ["Send one message to someone I like", "Show up to one thing for 20 minutes", "Name one way I'm similar to others"]
  },
  "Dependence / Incompetence": {
    script: "I can't handle life or make decisions without someone carrying the load.",
    surrender: "I handed the decision to someone else.",
    avoid: "I avoided the responsibility or new challenge.",
    overcompensate: "I refused any help and insisted on doing it all alone.",
    adaptive: "Self-Reliance & Autonomy",
    adaptiveScript: "I can trust my own compass \u2014 and asking for help is a choice, not a need.",
    flip: "Build confidence through small, independent choices and notice that you handled them.",
    reparent: "You are more capable than you feel right now. Let's take this one small step at a time \u2014 I believe in you.",
    actions: ["Make one small decision on my own", "Write down a problem I've solved before", "Try first, then ask for help"]
  },
  "Vulnerability to Harm or Illness": {
    script: "Disaster is around the corner and I won't survive it.",
    surrender: "I lived in dread and took excessive precautions.",
    avoid: "I avoided things that felt even a little risky.",
    overcompensate: "I over-prepared, controlled everything or sought constant reassurance.",
    adaptive: "Grounded Safety & Realistic Preparedness",
    adaptiveScript: "Right now, in this moment, I'm safe enough \u2014 and I can cope with what comes.",
    flip: "Return to present-moment safety and trust your coping resources instead of anticipating panic.",
    reparent: "You're safe right now. I'm here, I'm looking out for you, and we can handle what comes together.",
    actions: ["Name 3 things showing I'm safe right now", "Skip one reassurance check", "Do one small thing I've been avoiding"]
  },
  "Enmeshment / Undeveloped Self": {
    script: "I can't have an identity separate from my parent or partner.",
    surrender: "I took on their opinions and goals as my own.",
    avoid: "I avoided stating my own preference.",
    overcompensate: "I pushed people away hard to feel separate.",
    adaptive: "Healthy Individuation & Clear Boundaries",
    adaptiveScript: "I can be close to people and still be fully myself.",
    flip: "Cultivate your own preferences, voice and goals while staying lovingly connected.",
    reparent: "Your thoughts and wants matter in their own right. You can be you and still be loved.",
    actions: ["Name one preference that is only mine", "Make a choice without checking first", "Say 'I see it differently'"]
  },
  "Failure": {
    script: "I'm incompetent and I'll inevitably fail.",
    surrender: "I gave up early, sure I'd fail anyway.",
    avoid: "I procrastinated so my ability wouldn't be tested.",
    overcompensate: "I pushed frantically to prove it wrong, toward burnout.",
    adaptive: "Competence & Resilient Growth",
    adaptiveScript: "Setbacks are experience, not a final grade on my worth.",
    flip: "Reframe setbacks as lessons and trust your capacity to learn and adapt.",
    reparent: "You don't have to be perfect to be enough. Trying is brave \u2014 and I'm proud of you for showing up.",
    actions: ["Do a 10-minute 'good enough' version", "List 3 things I've learned from setbacks", "Share imperfect work with one person"]
  },
  "Entitlement / Grandiosity": {
    script: "I'm special and the usual rules shouldn't apply to me.",
    surrender: "I expected special treatment as my right.",
    avoid: "I avoided situations where I'm treated as an equal.",
    overcompensate: "I dominated, demanded status, or raged when told no.",
    adaptive: "Humility & Mutual Respect",
    adaptiveScript: "Other people's needs and boundaries matter as much as mine.",
    flip: "Practise egalitarian relationships where everyone's rights are respected equally.",
    reparent: "You don't need to be above anyone to be valued. You're worthy as an equal.",
    actions: ["Ask someone what they need", "Accept a 'no' gracefully once", "Follow one rule I'd usually skip"]
  },
  "Insufficient Self-Control / Self-Discipline": {
    script: "I shouldn't have to put up with discomfort, frustration or boredom.",
    surrender: "I gave in to the impulse or quit when it got uncomfortable.",
    avoid: "I avoided tasks that need patience.",
    overcompensate: "I set rigid, harsh rules on myself or others.",
    adaptive: "Healthy Frustration Tolerance & Persistence",
    adaptiveScript: "I can sit with this discomfort for a little while \u2014 it will pass.",
    flip: "Build patience to tolerate temporary discomfort for what matters long-term.",
    reparent: "It's okay to feel restless. I'll stay with you while it passes \u2014 you can do hard things.",
    actions: ["Ride the urge for 10 minutes", "Do 5 minutes of the boring task", "Set one gentle, realistic limit"]
  },
  "Subjugation": {
    script: "I have to give up my voice and wants to avoid conflict or rejection.",
    surrender: "I went along with what others wanted and stayed quiet.",
    avoid: "I avoided situations where I'd have to state an opinion.",
    overcompensate: "Pent-up resentment burst out as sudden anger.",
    adaptive: "Assertive Self-Expression",
    adaptiveScript: "My opinions and needs deserve space \u2014 I can say them calmly.",
    flip: "Honour your opinions, state boundaries clearly, and stop apologising for taking up space.",
    reparent: "Your voice matters. You're allowed to want things, and I'll stand with you when you say them.",
    actions: ["Say one preference out loud", "Use 'I need\u2026' once today", "Say no to one small request"]
  },
  "Self-Sacrifice": {
    script: "My only value is taking care of everyone else, even at my own cost.",
    surrender: "I ignored my limits to look after everyone else.",
    avoid: "I avoided letting anyone care for me.",
    overcompensate: "I kept score and felt resentful or martyred.",
    adaptive: "Balanced Self-Care & Reciprocal Boundaries",
    adaptiveScript: "Rest is a biological need \u2014 and receiving care is allowed.",
    flip: "Honour your physical and emotional limits and practise mutual give-and-take.",
    reparent: "You deserve care too. It's okay to rest \u2014 you don't have to earn it by giving everything away.",
    actions: ["Take a 15-minute guilt-free rest", "Let someone help me with one thing", "Decline one favour kindly"]
  },
  "Approval-Seeking / Recognition-Seeking": {
    script: "My worth depends on applause, status and pleasing others.",
    surrender: "I changed my opinions or looks to please people.",
    avoid: "I hid from visibility to dodge disapproval.",
    overcompensate: "I chased the spotlight or status markers.",
    adaptive: "Internal Validation & Authentic Expression",
    adaptiveScript: "I measure my day by my own values, not by others' reactions.",
    flip: "Ground accomplishment in your own values and self-approval rather than external metrics.",
    reparent: "You don't need anyone's applause to matter. I'm proud of you for who you are, not what you show.",
    actions: ["Do one thing just because I value it", "Post or share without checking reactions", "Praise myself for one thing"]
  },
  "Negativity / Pessimism": {
    script: "Everything's bound to go wrong, so expect the worst.",
    surrender: "I braced for the worst and dismissed the good.",
    avoid: "I avoided hope or trying, so I couldn't be disappointed.",
    overcompensate: "I worried, checked and over-planned to prevent every bad outcome.",
    adaptive: "Balanced Optimism & Realistic Perspective",
    adaptiveScript: "Some things may go wrong, and some things are going right.",
    flip: "Acknowledge challenges honestly while actively noticing what goes well.",
    reparent: "It makes sense you learned to expect the worst. Right now, let's notice what's okay together.",
    actions: ["Write 3 things that went okay today", "Name the most likely outcome, not the worst", "Let one good moment land fully"]
  },
  "Emotional Inhibition": {
    script: "I must hold my feelings in or I'll be judged or lose control.",
    surrender: "I swallowed my feelings and stayed flat or overly rational.",
    avoid: "I avoided situations where feelings might come up.",
    overcompensate: "I over-controlled, or feelings finally burst out all at once.",
    adaptive: "Emotional Openness & Spontaneity",
    adaptiveScript: "My feelings are safe to feel and share in small, chosen ways.",
    flip: "Let feelings move: name them, express a little with safe people, and allow play.",
    reparent: "You're allowed to feel \u2014 all of it. Your feelings won't push me away; I want to know them.",
    actions: ["Name the feeling out loud", "Share one feeling with a safe person", "Do something silly or playful for 5 minutes"]
  },
  "Unrelenting Standards / Hypercriticalness": {
    script: "I must be flawless and out-perform everyone to be worthy.",
    surrender: "I drove myself relentlessly and felt guilty resting.",
    avoid: "I procrastinated or hid from the challenge in case I fell short.",
    overcompensate: "I micro-managed, over-controlled or held others to impossible standards.",
    adaptive: "Self-Compassion & Human Acceptance",
    adaptiveScript: "My worth is fixed, not floating on my latest output. Good enough is enough.",
    flip: "Redefine success to include rest and limits \u2014 lower the bar from perfection to authentic contribution.",
    reparent: "You don't have to be perfect for me to love you. You're already enough \u2014 you can rest now.",
    actions: ["Make a 'trash track' \u2014 a deliberately imperfect version", "Write in pencil to bypass the critic", "Stop at 'good enough' and rest"]
  },
  "Punitiveness": {
    script: "Mistakes are unforgivable \u2014 someone has to be punished.",
    surrender: "I punished myself harshly for the mistake.",
    avoid: "I hid my mistake so I wouldn't face punishment.",
    overcompensate: "I lashed out and was harsh with others' mistakes.",
    adaptive: "Forgiveness & Grace",
    adaptiveScript: "Mistakes are how humans learn. I can repair instead of punish.",
    flip: "Treat errors as part of growth and swap self-attack for compassionate problem-solving.",
    reparent: "You made a mistake, and you're still good. Let's fix what we can, gently, together.",
    actions: ["Write what I'd say to a friend who made this mistake", "Choose one repair step", "Forgive one small thing today"]
  }
};
function schemaScript(name) {
  return SCHEMA_SCRIPTS[name] ?? null;
}
export {
  COPING_STYLES,
  CORE_NEEDS,
  SCHEMA_SCRIPTS,
  copingStyleLabel,
  coreNeed,
  needGroups,
  needLabel,
  needStatements,
  schemaScript
};

export function emptyFlip(schema, copingStyle = null) {
  return { schema, copingStyle, story: "", facts: "", reparent: schemaScript(schema)?.reparent ?? "", action: "", done: false };
}
export function flipStats(entries) {
  const counts = {};
  const styles = { surrender: 0, avoid: 0, overcompensate: 0, healthy: 0 };
  let actions = 0, done = 0, total = 0;
  entries.forEach((e) => e.flips.forEach((f) => {
    total += 1;
    counts[f.schema] = (counts[f.schema] ?? 0) + 1;
    if (f.copingStyle) styles[f.copingStyle] += 1;
    if (f.action.trim()) { actions += 1; if (f.done) done += 1; }
  }));
  const topSchemas = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 5);
  return { total, topSchemas, styles, actions, done };
}
